import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import type { EmailAppointmentDialogProps } from './types';
import { formatDatumDE } from './utils';

const EmailAppointmentDialog: React.FC<EmailAppointmentDialogProps> = ({
  open,
  appointments,
  summary,
  loading,
  savingToCalendar,
  onClose,
  onDownloadIcs,
  onSaveToCalendar,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarIcon />
          Termine aus E-Mail extrahieren
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography color="textSecondary" sx={{ mt: 2 }}>
              Analysiere E-Mail nach Terminen...
            </Typography>
          </Box>
        ) : (
          <>
            {summary && (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Zusammenfassung:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.light' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {summary}
                  </Typography>
                </Paper>
              </Box>
            )}

            {appointments.length === 0 ? (
              <Typography color="textSecondary" align="center" py={4}>
                Keine Termine in dieser E-Mail gefunden
              </Typography>
            ) : (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="subtitle2" color="primary">
                  Gefundene Termine ({appointments.length}):
                </Typography>
                {appointments.map((appointment, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{ p: 2 }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {appointment.titel}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => onDownloadIcs(appointment)}
                        >
                          ICS
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={savingToCalendar ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={() => onSaveToCalendar(appointment)}
                          disabled={savingToCalendar}
                        >
                          Kalender
                        </Button>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box display="grid" gridTemplateColumns="auto 1fr" gap={1} sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" color="textSecondary">Datum:</Typography>
                      <Typography variant="body2">{formatDatumDE(appointment.datum)}</Typography>

                      {appointment.uhrzeit && (
                        <>
                          <Typography variant="body2" color="textSecondary">Uhrzeit:</Typography>
                          <Typography variant="body2">{appointment.uhrzeit}</Typography>
                        </>
                      )}

                      {appointment.ort && (
                        <>
                          <Typography variant="body2" color="textSecondary">Ort:</Typography>
                          <Typography variant="body2">{appointment.ort}</Typography>
                        </>
                      )}

                      {appointment.beschreibung && (
                        <>
                          <Typography variant="body2" color="textSecondary">Beschreibung:</Typography>
                          <Typography variant="body2">{appointment.beschreibung}</Typography>
                        </>
                      )}

                      {appointment.istGanztaegig && (
                        <>
                          <Typography variant="body2" color="textSecondary">Typ:</Typography>
                          <Typography variant="body2">Ganztägig</Typography>
                        </>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailAppointmentDialog;
